import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Users, Loader2, UserMinus } from 'lucide-react';
import { toast } from 'sonner';
import WorldGlobe from '../components/ui/WorldGlobe';

const API_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api`;

const Team = () => {
  const { token, user, isAdmin } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Remove user state
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);

  useEffect(() => {
    fetchTeamMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${API_URL}/team/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamMembers(response.data);
    } catch (error) {
      // Non-admins will get 403, which is expected
      if (error.response?.status !== 403) {
        toast.error('Failed to load team members');
      }
    } finally {
      setLoading(false);
    }
  };

  // Remove user handlers
  const handleRemoveClick = (member) => {
    setUserToRemove(member);
    setRemoveDialogOpen(true);
  };

  const handleRemoveConfirm = async () => {
    if (!userToRemove || isRemoving) return;
    
    setIsRemoving(true);
    try {
      await axios.delete(`${API_URL}/team/users/${userToRemove.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('User removed');
      setRemoveDialogOpen(false);
      setUserToRemove(null);
      
      // Refresh team list
      await fetchTeamMembers();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Only admins can remove users');
      } else if (error.response?.status === 404) {
        toast.error('User not found');
      } else if (error.response?.status === 400) {
        toast.error('You cannot remove yourself');
      } else {
        toast.error('Failed to remove user');
      }
    } finally {
      setIsRemoving(false);
    }
  };

  const handleRemoveCancel = () => {
    setRemoveDialogOpen(false);
    setUserToRemove(null);
  };

  return (
    <div className="space-y-6 relative" data-testid="team-page">
      <WorldGlobe size={300} className="hidden lg:block opacity-70" style={{ top: '-50px', right: '-30px' }} />
      <Card className="world-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg flex items-center justify-center world-icon-box">
                <Users className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <CardTitle className="text-xl text-white">Team</CardTitle>
                <CardDescription className="text-slate-400">
                  Manage your team members
                </CardDescription>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!isAdmin ? (
            <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="non-admin-message">
              <div className="h-16 w-16 rounded-full bg-white/10 flex items-center justify-center mb-4">
                <Users className="h-8 w-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">
                Admin access required
              </h3>
              <p className="text-slate-400 max-w-md">
                Only team administrators can view and manage team members.
              </p>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-400" />
            </div>
          ) : teamMembers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="h-16 w-16 rounded-full bg-white/10 flex items-center justify-center mb-4">
                <Users className="h-8 w-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">
                No team members yet
              </h3>
              <p className="text-slate-400 max-w-md">
                New members join by platform invitation during the private pilot.
              </p>
            </div>
          ) : (
            <>
            <div className="divide-y divide-white/5">
              {teamMembers.map((member) => (
                <div 
                  key={member.id} 
                  className="flex items-center justify-between gap-3 py-4"
                  data-testid={`team-member-${member.id}`}
                >
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="h-10 w-10 shrink-0 rounded-full bg-blue-500/15 flex items-center justify-center">
                      <span className="text-sm font-medium text-blue-400">
                        {member.email[0].toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-medium text-white break-all">
                          {member.email}
                        </p>
                        {member.status === 'invited' && (
                          <span 
                            className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/15 text-amber-300"
                            data-testid={`invited-badge-${member.id}`}
                          >
                            Invited
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400">
                        {member.status === 'invited' 
                          ? `Invited ${new Date(member.created_at).toLocaleDateString()}`
                          : `Joined ${new Date(member.created_at).toLocaleDateString()}`
                        }
                      </p>
                      {/* Role and "(You)" sit under the email/date, on the left: the right edge
                          of the card is where the WorldGlobe artwork is drawn. */}
                      <div className="mt-1.5 flex flex-wrap items-center gap-2" data-testid={`member-meta-${member.id}`}>
                        <span
                          className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            member.role === 'admin' 
                              ? 'bg-blue-500/15 text-blue-300' 
                              : 'bg-white/10 text-slate-200'
                          }`}
                          data-testid={`member-role-${member.id}`}
                        >
                          {member.role}
                        </span>
                        {member.id === user?.id && (
                          <span className="text-xs text-slate-400" data-testid={`member-self-${member.id}`}>(You)</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {member.id !== user?.id && (
                    <div className="flex shrink-0 items-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveClick(member)}
                        className="h-8 px-2 text-slate-400 hover:text-red-600 hover:bg-red-500/15"
                        data-testid={`remove-btn-${member.id}`}
                      >
                        <UserMinus className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {/* PILOT-ACCESS-003A: Company-admin self-service invitations belong to a later SaaS
                flow; during the private pilot members join only by platform invitation. Shown
                once, under the list, aligned with the member details (avatar 2.5rem + gap 0.75rem). */}
            <p className="pt-4 pl-[3.25rem] text-sm text-slate-400" data-testid="team-private-pilot-notice">
              Private pilot - new members join by platform invitation.
            </p>
            </>
          )}
        </CardContent>
      </Card>
      
      {/* Remove User Confirmation Dialog */}
      <AlertDialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
        <AlertDialogContent data-testid="remove-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove team member?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>This will remove <span className="font-medium">{userToRemove?.email}</span> from your team.</p>
              <p className="text-slate-300 font-medium">They will lose access immediately.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={handleRemoveCancel}
              disabled={isRemoving}
              data-testid="remove-cancel-btn"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveConfirm}
              disabled={isRemoving}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              data-testid="remove-confirm-btn"
            >
              {isRemoving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Removing...
                </>
              ) : (
                'Remove'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Team;
